import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ProductType} from '@wix/wixstores-client-core/dist/es/src/types';
import {MobileDrawerWrapper} from '@wix/wixstores-client-storefront-common/components';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src';
import React, {useCallback} from 'react';
import stylesParams from '../../../../../components/ProductPage/stylesParams';
import {TextualOptionType} from '../../../../constants';
import {useGlobals, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {RenderContextProvider} from '../../../../providers/renderContext';
import {IProductDTO} from '../../../../types/app-types';
import {OptionsButtons} from '../../../ProductOptions/OptionsButton/OptionsButtons';
import {OptionsDropdown} from '../../../ProductOptions/OptionsDropdown/OptionsDropdown';
import {ProductColors} from '../../../ProductOptions/ProductColors/ProductColors';
import {ProductCustomText} from '../../../ProductOptions/ProductCustomText/ProductCustomText';
import {ProductQuantity} from '../../../ProductOptions/ProductQuantity/ProductQuantity';
import {StockIndicator} from '../../../ProductOptions/StockIndicator/StockIndicator';
import {SubscriptionPlans} from '../../../ProductOptions/SubscriptionPlans/SubscriptionPlans';
import {StickyButton} from '../StickyButton/StickyButton';
import s from './StickyOptionsDrawer.scss';
import {classes} from './StickyOptionsDrawer.st.css';

export enum StickyOptionsDrawerDataHook {
  Root = 'StickyOptionsDrawerDataHook.Root',
  Content = 'StickyOptionsDrawerContentDataHook.Root',
  Options = 'StickyOptionsDrawerContentDataHook.Options',
  AddToCartButton = 'StickyOptionsDrawerContentDataHook.AddToCartButton',
  QuantityCounter = 'StickyOptionsDrawerContentDataHook.QuantityCounter',
  SubscriptionPlansContainer = 'StickyOptionsDrawerContentDataHook.SubscriptionPlans',
}

export type StickyOptionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  buttonText: string;
} & IProvidedTranslationProps;

function renderOption(option: IProductDTO['options'][number], index: number, useButtons: boolean): React.ReactElement {
  if (option.optionType === ProductOptionType.COLOR) {
    return (
      <div className={s.optionContainer} key={`option-${index}`}>
        <ProductColors optionIndex={index} showErrors={false} showTooltips={false} />
      </div>
    );
  }

  return (
    <div className={s.optionContainer} key={`option-${index}`}>
      {useButtons ? (
        <OptionsButtons optionIndex={index} showErrors={false} />
      ) : (
        <OptionsDropdown optionIndex={index} showErrors={false} />
      )}
    </div>
  );
}

const StickyOptionsDrawerComponent = ({isOpen, onClose, buttonText, t}: StickyOptionsDrawerProps) => {
  const {host, product, handleAddToCart, styles, shouldShowSubscriptionPlans, isPreOrderState, isUserInputsValid} =
    useGlobals();
  const textualOptionType = styles.get(stylesParams.productPage_textualOptionType);
  const useButtons = textualOptionType === TextualOptionType.BUTTONS;
  const isPhysicalProduct = product?.productType === ProductType.PHYSICAL;

  const handleAddToCartClick = useCallback(() => {
    handleAddToCart();
    onClose();
  }, [handleAddToCart, onClose]);

  function renderCustomTextFields() {
    const customTextFields = product?.customTextFields;
    if (!customTextFields?.length) {
      return null;
    }

    return customTextFields.map((customTextField, index) => (
      <div className={s.optionContainer} key={`custom-text-${index}`}>
        <ProductCustomText
          errorIndex={index}
          title={customTextField.title}
          isRequired={customTextField.isMandatory}
          maxLength={customTextField.inputLimit}
          className={classes.compactTextArea}
          showErrors={false}
        />
      </div>
    ));
  }

  function renderQuantity() {
    return (
      <div className={s.optionContainer} data-hook={StickyOptionsDrawerDataHook.QuantityCounter}>
        <ProductQuantity className={s.quantityCounter} showErrors={false} />
      </div>
    );
  }

  function renderSubscriptionPlans() {
    return (
      <div className={s.optionContainer} data-hook={StickyOptionsDrawerDataHook.SubscriptionPlansContainer}>
        <SubscriptionPlans className={s.subscriptionPlans} showErrors={false} />
      </div>
    );
  }

  const drawerButtonText = isUserInputsValid ? buttonText : t('productPage.selectOptions.header.greyCta');
  const disabled = !isUserInputsValid;

  return (
    <MobileDrawerWrapper
      isOpen={isOpen}
      onRequestClose={onClose}
      dataHook={StickyOptionsDrawerDataHook.Root}
      hostId={host.id}>
      <RenderContextProvider value="stickyDrawer">
        <div className={s.root} data-hook={StickyOptionsDrawerDataHook.Content}>
          <div className={s.scrollableContainer} data-hook={StickyOptionsDrawerDataHook.Options}>
            {product?.options?.map((option, index) => renderOption(option, index, useButtons))}
            {renderCustomTextFields()}
            {isPhysicalProduct && renderQuantity()}
            {!isPreOrderState && <StockIndicator />}
            {shouldShowSubscriptionPlans && renderSubscriptionPlans()}
          </div>
          <div className={s.addToCartButtonContainer}>
            <StickyButton text={drawerButtonText} onClick={handleAddToCartClick} disabled={disabled} />
          </div>
        </div>
      </RenderContextProvider>
    </MobileDrawerWrapper>
  );
};

export const StickyOptionsDrawer = withGlobalProps(withTranslations('globals.texts')(StickyOptionsDrawerComponent));
